import React, { useState } from 'react';
import { authFetch } from '../services/api';

// VIDEO-HERO-FEATURE, Part 1 (upload/storage) — mirrors ReferenceDesignModal's
// two-tab (upload vs. paste-link) shape and styling, but this modal doesn't
// trigger generation itself: it only produces a {videoUrl, posterUrl} pair
// that ProjectChat.js attaches to the NEXT message sent (message-scoped,
// same as a design-reference screenshot — see Job.hero_video_url's own
// comment in db/models.py). The video's bytes never touch this app's
// backend: file uploads go straight from the browser to GCS via a signed
// URL (see api/uploads.py's /video/signed-url), and the LLM/agent never
// sees anything but the resulting URL string.
//
// PLAIN-HERO-FEATURE: third tab, "No hero visual" — the generation prompt
// otherwise always mandates a full-width image hero background (RULE 10 in
// single_shot_generator.py), with no reliable way to opt out of it from
// prompt wording alone. Produces {heroStyle: 'none'} instead of a video —
// same message-scoped attachment mechanism, reusing the already-built
// _build_hero_override_block "none" case (see Job.hero_style's comment).
const MAX_VIDEO_BYTES = 30 * 1024 * 1024; // 30MB — matches backend's MAX_VIDEO_SIZE
const ALLOWED_EXT = ['.mp4', '.webm'];

export default function HeroVideoModal({ onClose, onAttached }) {
  const [mode, setMode] = useState('upload'); // 'upload' | 'url' | 'none'
  const [file, setFile] = useState(null);
  const [pastedUrl, setPastedUrl] = useState('');
  const [status, setStatus] = useState('idle'); // idle | uploading | posterizing | done | error
  const [error, setError] = useState('');
  const [videoUrl, setVideoUrl] = useState(null);
  const [posterUrl, setPosterUrl] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError('');
    setVideoUrl(null);
    setPosterUrl(null);
    const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setError(`Unsupported file type. Allowed: ${ALLOWED_EXT.join(', ')}`);
      return;
    }
    if (f.size > MAX_VIDEO_BYTES) {
      setError(`Video too large (max ${MAX_VIDEO_BYTES / (1024 * 1024)}MB) — hero videos should be short loops.`);
      return;
    }
    setFile(f);
    uploadDirect(f, ext);
  };

  const uploadDirect = async (f, ext) => {
    setStatus('uploading');
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', f);
      const uploadRes = await authFetch('/api/upload/video', {
        method: 'POST',
        body: formData,
      });
      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const { url: public_url } = await uploadRes.json();

      // Validate the returned URL is publicly reachable (GCS or a real public host).
      // A localhost/relative URL will silently fail in the generated site preview.
      if (!public_url || public_url.startsWith('/') || public_url.includes('localhost') || public_url.includes('127.0.0.1')) {
        throw new Error(
          'Video was saved locally but cannot be used in the preview — GCS storage is not configured on this server. ' +
          'Please ask your admin to set GCS_BUCKET_NAME, GOOGLE_APPLICATION_CREDENTIALS, and GOOGLE_CLOUD_PROJECT in the server .env.'
        );
      }

      setVideoUrl(public_url);
      setStatus('posterizing');

      // Best-effort — a missing poster never blocks attaching the video.
      try {
        const posterRes = await authFetch('/api/upload/video/generate-poster', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ video_url: public_url }),
        });
        if (posterRes.ok) {
          const { poster_url } = await posterRes.json();
          if (poster_url) setPosterUrl(poster_url);
        }
      } catch { /* poster is optional, ignore */ }

      setStatus('done');
    } catch (e) {
      setError(e.message || 'Upload failed');
      setStatus('error');
    }
  };

  const canAttach = mode === 'none' || status === 'done' || (mode === 'url' && pastedUrl.trim());

  const handleAttach = () => {
    if (mode === 'upload') {
      if (!videoUrl) return;
      onAttached({ videoUrl, posterUrl, heroStyle: null });
    } else if (mode === 'url') {
      onAttached({ videoUrl: pastedUrl.trim(), posterUrl: null, heroStyle: null });
    } else {
      onAttached({ videoUrl: null, posterUrl: null, heroStyle: 'none' });
    }
    onClose();
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.header}>
          <h3 style={s.title}>🎬 Hero video &amp; style</h3>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={s.tabs}>
          <button style={{ ...s.tab, ...(mode === 'upload' ? s.tabActive : {}) }} onClick={() => setMode('upload')}>Upload Video</button>
          <button style={{ ...s.tab, ...(mode === 'url' ? s.tabActive : {}) }} onClick={() => setMode('url')}>Video URL</button>
          <button style={{ ...s.tab, ...(mode === 'none' ? s.tabActive : {}) }} onClick={() => setMode('none')}>No Hero Visual</button>
        </div>

        {mode === 'upload' ? (
          <div style={s.field}>
            <label style={s.label}>Video file (.mp4, .webm — max 30MB, keep it a short loop)</label>
            <input type="file" accept=".mp4,.webm" onChange={handleFileChange} style={s.fileInput} disabled={status === 'uploading' || status === 'posterizing'} />
            {status === 'uploading' && <p style={s.hint}>Uploading…</p>}
            {status === 'posterizing' && <p style={s.hint}>✓ Uploaded. Generating a thumbnail…</p>}
            {status === 'done' && (
              <p style={{ ...s.hint, color: '#22c55e' }}>
                ✓ Ready{posterUrl ? ' (with thumbnail)' : ' (no thumbnail — a default will be used)'}
              </p>
            )}
            {posterUrl && <img src={posterUrl} alt="hero video thumbnail" style={s.preview} />}
          </div>
        ) : mode === 'url' ? (
          <div style={s.field}>
            <label style={s.label}>URL of an already-hosted video</label>
            <input
              type="text"
              value={pastedUrl}
              onChange={e => setPastedUrl(e.target.value)}
              placeholder="https://cdn.example.com/hero.mp4"
              style={s.textInput}
            />
            <p style={s.hint}>Used as-is — no upload needed.</p>
          </div>
        ) : (
          <div style={s.field}>
            <p style={s.hint}>
              No background image or video for the hero section — a plain
              solid/gradient background with your headline, subtext, and
              buttons instead. Overrides the default full-width image hero
              for this message only.
            </p>
          </div>
        )}

        {error && <div style={s.error}>{error}</div>}

        <div style={s.actions}>
          <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={{ ...s.submitBtn, ...(canAttach ? {} : s.submitBtnDisabled) }} onClick={handleAttach} disabled={!canAttach}>
            Attach to next message
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, width: 440, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 14 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0 },
  closeBtn: { background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 16, cursor: 'pointer', padding: 4 },
  tabs: { display: 'flex', gap: 6, background: 'var(--bg-card)', padding: 4, borderRadius: 8 },
  tab: { flex: 1, padding: '8px 10px', background: 'transparent', border: 'none', borderRadius: 6, color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  tabActive: { background: '#6366f1', color: '#fff' },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' },
  fileInput: { fontSize: 12, color: 'var(--text-primary)' },
  preview: { maxWidth: '100%', maxHeight: 160, borderRadius: 8, border: '1px solid var(--border)', objectFit: 'cover' },
  textInput: { padding: '10px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, outline: 'none' },
  hint: { fontSize: 11, color: 'var(--text-muted)', margin: 0 },
  error: { fontSize: 12, color: '#ef4444', background: 'rgba(239,68,68,0.1)', padding: '8px 10px', borderRadius: 6 },
  actions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  cancelBtn: { padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' },
  submitBtn: { padding: '8px 16px', background: '#6366f1', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  submitBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
};
